import { Box, Spinner } from '@ouster/webviz-react-components';

import { Popover } from '@mui/material';
import { useEffect, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { useStoreActions, useStoreState } from '../../reducers';
import { DownloadContent } from '../DriveNavBar';

type RecordingDownloadPopoverProps = {
  anchorEl: HTMLElement | null;
  closeDownloadMenu: () => void;
  downloadRecordingId: string;
  orgId?: number;
  isPublic?: boolean;
  recordingTitle?: string;
};

export default function RecordingDownloadPopover({
  anchorEl,
  closeDownloadMenu,
  downloadRecordingId,
  orgId,
  isPublic = false,
  recordingTitle,
}: RecordingDownloadPopoverProps) {
  const [isLoadingDownloadInfo, setIsLoadingDownloadInfo] = useState(false);
  const { loadSourceFileInfo, loadOutputFileInfo } = useStoreActions(
    (actions) => actions.drive
  );
  const { showToast } = useChakraHook();
  const {
    outputForDownload,
    outputForDownloadNonSlam,
    sourceDataForDownload,
    sourceConfigForDownload,
  } = useStoreState((storeState) => storeState.drive);

  const open = Boolean(anchorEl);

  useEffect(() => {
    const loadDownloadInfo = async () => {
      try {
        setIsLoadingDownloadInfo(true);
        await loadOutputFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });

        await loadSourceFileInfo({
          id: downloadRecordingId,
          isPrivate: !isPublic,
          orgId,
        });
      } catch (error) {
        // console.error('Error loading download info:', error);
        showToast({
          title: 'Failed to download recording',
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
        closeDownloadMenu();
      } finally {
        setIsLoadingDownloadInfo(false);
      }
    };

    if (orgId && downloadRecordingId) loadDownloadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloadRecordingId,
    isPublic,
    loadOutputFileInfo,
    loadSourceFileInfo,
    orgId,
    showToast,
  ]);

  const id = open ? 'download-popover' : undefined;

  const hasDownloadableFiles =
    sourceConfigForDownload ||
    sourceDataForDownload.length > 0 ||
    outputForDownload.length > 0 ||
    outputForDownloadNonSlam.length > 0;

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={closeDownloadMenu}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      data-testid="recording-download-popover-root"
    >
      <Box p="16px" display="flex" gap="12px" alignItems="flex-start">
        {isLoadingDownloadInfo && (
          <Spinner data-testid="recording-download-popover-loading-spinner" />
        )}
        {!isLoadingDownloadInfo && hasDownloadableFiles && (
          <DownloadContent
            sourceDataForDownload={sourceDataForDownload}
            sourceConfigForDownload={sourceConfigForDownload}
            downloadableOutputFiles={
              outputForDownload.length > 0
                ? outputForDownload
                : outputForDownloadNonSlam
            }
            driveIdFromList={downloadRecordingId}
            isPublic={isPublic}
            driveOrgId={orgId}
            recordingTitle={recordingTitle}
          />
        )}
        {!isLoadingDownloadInfo && !hasDownloadableFiles && (
          <Box lineHeight={1.6} maxW={120}>
            No files available for download
          </Box>
        )}
      </Box>
    </Popover>
  );
}
